import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Download } from "lucide-react";
import { postWebsiteLead } from "@/lib/leadCapture";
import { sanitizeNameInput } from "@/lib/formInput";
import PhoneInput, { isValidPhone } from "@/components/ui-shared/PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrochureModal = ({ open, onClose, courseTitle, courseId, vendorSlug, vendorName, brochureUrl }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", jobTitle: "" });
    const [loading, setLoading] = useState(false);
    if (!open) return null;
    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.jobTitle.trim()) {
            toast.error("Please fill in all the fields.");
            return;
        }
        if (!isValidPhone(form.phone)) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        postWebsiteLead({ ...form, course: courseTitle, vendor: vendorName, vendor_slug: vendorSlug }, "brochure-request");

        // A real brochure PDF is bundled with the site — download it directly,
        // no backend required. Lead capture already went to the ERP above.
        if (brochureUrl) {
            const a = document.createElement("a");
            a.href = brochureUrl;
            a.download = `SpringXE-${vendorSlug}-${courseTitle || "brochure"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success("Brochure is downloading. Our team will reach out shortly.");
            setForm({ name: "", email: "", phone: "", company: "", jobTitle: "" });
            setLoading(false);
            onClose();
            return;
        }

        if (!process.env.REACT_APP_BACKEND_URL) {
            toast.success("Thanks! Our team will email you this brochure shortly.");
            setForm({ name: "", email: "", phone: "", company: "", jobTitle: "" });
            setLoading(false);
            onClose();
            return;
        }

        try {
            const res = await axios.post(`${API}/brochure-request`, {
                ...form,
                course: courseTitle,
                vendor: vendorName,
                vendor_slug: vendorSlug,
            });
            const dl = res.data.download_url || `/api/brochure/${vendorSlug}.pdf`;
            const qs = new URLSearchParams();
            if (courseTitle) qs.set("course", courseTitle);
            if (vendorName) qs.set("vendor", vendorName);
            if (courseId) qs.set("course_id", courseId);
            const sep = dl.includes("?") ? "&" : "?";
            const url = `${process.env.REACT_APP_BACKEND_URL}${dl}${qs.toString() ? sep + qs.toString() : ""}`;
            const a = document.createElement("a");
            a.href = url;
            a.download = `SpringXE-${vendorSlug}-brochure.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success("Brochure is downloading. Our team will reach out shortly.");
            setForm({ name: "", email: "", phone: "", company: "", jobTitle: "" });
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to prepare your brochure.");
        } finally { setLoading(false); }
    };
    return (
        <div data-testid="brochure-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-up" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="w-full max-w-lg sxe-form-card rounded-2xl p-6 md:p-8 relative">
                <button type="button" onClick={onClose} aria-label="Close" data-testid="brochure-modal-close" className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#150826]/10 text-[#150826] hover:border-[#A100FF]/50 hover:bg-[#A100FF]/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center"><Download className="w-6 h-6 text-white" /></div>
                <h3 className="font-display text-[#150826] text-2xl md:text-3xl font-medium tracking-tight mt-5">Download course brochure</h3>
                {courseTitle && <p className="text-[#7B00CC] text-sm mt-1">{courseTitle}</p>}
                <p className="text-[#6B5980] text-sm mt-2">Share a few details and we&apos;ll open the brochure instantly, plus keep you posted on cohort dates.</p>
                <div className="grid md:grid-cols-2 gap-3 mt-6">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Full Name*</label>
                        <input data-testid="brochure-input-name" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: sanitizeNameInput(e.target.value) })} className="sxe-form-input mt-1" placeholder="Your name" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Business Email*</label>
                        <input data-testid="brochure-input-email" name="email" type="email" value={form.email} onChange={onChange} className="sxe-form-input mt-1" placeholder="you@company.com" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Phone Number*</label>
                        <PhoneInput testId="brochure-input-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Company Name*</label>
                        <input data-testid="brochure-input-company" name="company" value={form.company} onChange={onChange} className="sxe-form-input mt-1" placeholder="Company name" required />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Job Title*</label>
                        <input data-testid="brochure-input-jobtitle" name="jobTitle" value={form.jobTitle} onChange={onChange} className="sxe-form-input mt-1" placeholder="e.g. Head of L&amp;D" required />
                    </div>
                </div>
                <button type="submit" disabled={loading} data-testid="brochure-submit-btn" className="btn-primary w-full mt-6 disabled:opacity-60">
                    {loading ? "Preparing your brochure..." : (<>Download Brochure <Download className="w-4 h-4" /></>)}
                </button>
            </form>
        </div>
    );
};

export default BrochureModal;
